import { AddBlock, NoResults } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKButton,
  CKCheckboxNext,
  CKLayoutHorizontal,
  CKMultiPickerNextProps,
  CKSearch,
  CKSelectableItem,
} from 'clearkit';
import { FC, ReactNode, useState } from 'react';

type CKSelectableAccessoryItem = CKSelectableItem & {
  accessory?: ReactNode;
};

export type ListBoxMultiSelectProps = Omit<
  CKMultiPickerNextProps,
  'value' | 'onChange' | 'items'
> & {
  hasBorder?: boolean;
  hasFilter?: boolean;
  items?: CKSelectableAccessoryItem[] | undefined;
  maxHeight?: string;
  onChange: (value: string[]) => void;
  value: string[] | undefined;
};

export const ListBoxMultiSelect: FC<ListBoxMultiSelectProps> = ({
  autoFocus,
  canAddCustomValue,
  hasBorder,
  hasFilter = true,
  items,
  onChange,
  value = [],
  maxHeight = '12rem',
}) => {
  const [filter, setFilter] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<typeof items | undefined>(
    items,
  );

  const handleOnChange = (newItem: string) => {
    if (value?.includes(newItem)) {
      onChange(value?.filter((item) => item !== newItem));
    } else {
      onChange(value ? [...value, newItem] : [newItem]);
    }
  };

  const handleFilter = (query: string) => {
    setFilter(query);
    setFilteredItems(
      items?.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      ),
    );
  };

  const handleAddCustomValue = () => {
    if (filter) {
      onChange([...value, filter]);
      setFilter('');
      setFilteredItems(items);
    }
  };

  const style = {
    maxHeight,
  };

  return (
    <div className="pt-2 space-y-1">
      {hasFilter ? (
        <header className="p-2 pt-0">
          <CKSearch
            autoFocus={autoFocus}
            onChange={handleFilter}
            value={filter}
          />
        </header>
      ) : null}
      {canAddCustomValue && filter ? (
        <div className="px-4">
          <CKButton
            className="w-full !py-1"
            leftIcon={<AddBlock />}
            onClick={handleAddCustomValue}
            variant="tertiary"
            variantColor="blue"
          >
            Add <strong>"{filter}"</strong> as a custom value
          </CKButton>
        </div>
      ) : null}
      <div
        className={classnames(
          'min-w-0 min-h-0 ck-scrollbar ck-scrollbar--vertical space-y-1',
          {
            'px-4 pb-4 ': !hasBorder,
            'border p-2 border-gray-200 rounded-sm': hasBorder,
          },
        )}
        style={style}
      >
        {filteredItems && filteredItems.length > 0 ? (
          filteredItems?.map((item) => (
            <CKCheckboxNext
              checked={value?.includes(item.value)}
              className="text-sm"
              key={item.label}
              onChange={() => handleOnChange(item.value)}
            >
              <CKLayoutHorizontal
                as="span"
                className="w-full pt-0.5"
                gap=".5rem"
              >
                <span>{item.label}</span>
                {item.accessory || null}
              </CKLayoutHorizontal>
            </CKCheckboxNext>
          ))
        ) : (
          <CKLayoutHorizontal
            className="py-6 text-gray-500"
            gap=".5rem"
            justifyContent="center"
          >
            <NoResults height={18} width={18} />
            <p>
              No results for <strong>"{filter}"</strong>
            </p>
          </CKLayoutHorizontal>
        )}
      </div>
    </div>
  );
};
